import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './ActiveIntegrations.css';
import axios from 'axios';

const ActiveIntegrations = () => {
    const [integrations, setIntegrations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [processingId, setProcessingId] = useState(null);

    useEffect(() => {
        // Fetch integrations from backend
        const fetchIntegrations = async () => {
            try {
                setLoading(true);
                const response = await axios.get(`${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/api/employees/integrations`);
                setIntegrations(response.data);
                setLoading(false);
            } catch (err) {
                console.error('Error fetching integrations:', err);
                setError('Failed to fetch integrations');
                setLoading(false);
            }
        };

        fetchIntegrations();
    }, []);

    const handleManualProcess = async (integrationId) => {
        try {
            setProcessingId(integrationId);
            const response = await axios.post(`${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/api/employees/integration/${integrationId}/process`);
            alert(`Processed integration. Created ${response.data.formsCreated} new forms.`);
            setProcessingId(null);
        } catch (err) {
            console.error('Error processing integration:', err);
            alert('Failed to process integration. Check console for details.');
            setProcessingId(null);
        }
    };

    const handleCheckStatus = async (integrationId) => {
        try {
            setProcessingId(integrationId);
            const response = await axios.post(`${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/api/employees/integration/${integrationId}/check-status`);
            alert(`Checked form status. Updated ${response.data.formsUpdated} forms.`);
            setProcessingId(null);
        } catch (err) {
            console.error('Error checking form status:', err);
            alert('Failed to check form status. Check console for details.');
            setProcessingId(null);
        }
    };

    const handleKillIntegration = async (integrationId) => {
        // Confirm with the user before proceeding
        const confirmed = window.confirm(
            "Are you sure you want to kill this integration? This will delete all associated data and cannot be undone."
        );
        
        if (!confirmed) {
            return;
        }
        
        try {
            setProcessingId(integrationId);
            const response = await axios.delete(`${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/api/employees/integration/${integrationId}`);
            
            // Remove the integration from the list
            setIntegrations(prevIntegrations => 
                prevIntegrations.filter(integration => integration.id !== integrationId)
            );
            
            alert(`Integration killed successfully. ${response.data.message}`);
            setProcessingId(null);
        } catch (err) {
            console.error('Error killing integration:', err);
            alert('Failed to kill integration. Check console for details.');
            setProcessingId(null);
        }
    };

    if (loading) return (
        <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading integrations...</p>
        </div>
    );
    
    if (error) return (
        <div className="error-state">
            <p>{error}</p>
        </div>
    );

    return (
        <div className="active-integrations">
            <h1 className="page-title">Active Integrations</h1>
            {integrations.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-icon">📋</div>
                    <h2 className="empty-title">No Active Integrations</h2>
                    <p className="empty-message">
                        You haven't created any integrations yet. Start by creating your first integration.
                    </p>
                    <Link to="/" className="btn btn-primary">
                        Create Integration
                    </Link>
                </div>
            ) : (
                <div className="integration-list">
                    {integrations.map((integration) => (
                        <div key={integration.id} className="integration-card">
                            <div className="integration-header">
                                <h2 className="integration-title">{integration.companyName}</h2>
                                <span className={`status-badge ${integration.status}`}>
                                    {integration.status}
                                </span>
                            </div>
                            
                            <div className="integration-content">
                                <div className="integration-details">
                                    <p className="detail-item">
                                        <span className="detail-label">API Token:</span> {integration.apiToken.substring(0, 10)}...
                                    </p>
                                    <p className="detail-item">
                                        <span className="detail-label">Required Days:</span> {integration.requiredDays}
                                    </p>
                                    <p className="detail-item">
                                        <span className="detail-label">S3 Location:</span> {integration.folderPath}
                                    </p>
                                    <p className="detail-item">
                                        <span className="detail-label">Created:</span> {new Date(integration.createdAt).toLocaleString()}
                                    </p>
                                    
                                    {integration.employeeFormId && (
                                        <p className="detail-item">
                                            <span className="detail-label">Employee Form ID:</span> {integration.employeeFormId}
                                        </p>
                                    )}
                                    
                                    {integration.managerFormId && (
                                        <p className="detail-item">
                                            <span className="detail-label">Manager Form ID:</span> {integration.managerFormId}
                                        </p>
                                    )}
                                </div>
                                
                                <div className="integration-options">
                                    <h3 className="options-title">Upload Configuration</h3>
                                    <div className="options-grid">
                                        <div className="option-item">
                                            <span className={`option-icon ${integration.uploadOptions.uploadToManager ? 'enabled' : 'disabled'}`}>
                                                {integration.uploadOptions.uploadToManager ? '✓' : '✗'}
                                            </span>
                                            Upload to Manager
                                        </div>
                                        <div className="option-item">
                                            <span className={`option-icon ${integration.uploadOptions.uploadToSecondManager ? 'enabled' : 'disabled'}`}>
                                                {integration.uploadOptions.uploadToSecondManager ? '✓' : '✗'}
                                            </span>
                                            Upload to Second Manager
                                        </div>
                                        <div className="option-item">
                                            <span className={`option-icon ${integration.uploadOptions.uploadManagerForm ? 'enabled' : 'disabled'}`}>
                                                {integration.uploadOptions.uploadManagerForm ? '✓' : '✗'}
                                            </span>
                                            Upload Manager Form
                                        </div>
                                        <div className="option-item">
                                            <span className={`option-icon ${integration.uploadOptions.uploadSecondManagerForm ? 'enabled' : 'disabled'}`}>
                                                {integration.uploadOptions.uploadSecondManagerForm ? '✓' : '✗'}
                                            </span>
                                            Upload Second Manager Form
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="integration-actions">
                                <Link to={`/integration/${integration.id}`} className="view-link">
                                    View Details
                                </Link>
                                <button
                                    className="btn btn-success btn-sm"
                                    onClick={() => handleManualProcess(integration.id)}
                                    disabled={processingId === integration.id}
                                >
                                    {processingId === integration.id ? (
                                        <>
                                            <div className="loading-spinner"></div>
                                            Processing...
                                        </>
                                    ) : (
                                        'Manual Trigger'
                                    )}
                                </button>
                                <button
                                    className="btn btn-info btn-sm"
                                    onClick={() => handleCheckStatus(integration.id)}
                                    disabled={processingId === integration.id}
                                >
                                    {processingId === integration.id ? (
                                        <>
                                            <div className="loading-spinner"></div>
                                            Checking...
                                        </>
                                    ) : (
                                        'Check Status'
                                    )}
                                </button>
                                <a
                                    href={`${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/api/employees/integration/${integration.id}/export`}
                                    className="btn btn-secondary btn-sm"
                                    download
                                >
                                    📊 Export Excel
                                </a>
                                <button
                                    className="btn btn-danger btn-sm"
                                    onClick={() => handleKillIntegration(integration.id)}
                                    disabled={processingId === integration.id}
                                >
                                    Kill Integration
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ActiveIntegrations;